import { addYears, differenceInYears, isFuture } from 'date-fns';
import { parse } from 'date-fns/parse';
import { z } from 'zod';
import { LABELS } from '../../constants';
import {
  isValidDate,
  requiredFieldError,
  requiredNumberString,
  requiredString,
  validateDriversLicenseCardNumber,
} from '../../helper';
import { AddressState, IdentificationType, MedicareCardColour } from '../../types';

const {
  IDENTIFICATION_TYPE,
  LICENCE_NUMBER,
  LICENCE_CARD_NUMBER,
  MEDICARE_NUMBER,
  MEDICARE_REFERENCE,
  PASSPORT_NUMBER,
  ISSUING_COUNTRY,
  EXPIRY_DATE,
  PHOTO_ID_NUMBER,
} = LABELS;

const expiry = z.union([
  z
    .date({ required_error: requiredFieldError(EXPIRY_DATE), invalid_type_error: requiredFieldError(EXPIRY_DATE) })
    .refine(
      value => isFuture(value) && Math.abs(differenceInYears(addYears(new Date(), 10), value)) <= 10,
      'Enter a valid expiry date in the future',
    ),
  z.string().refine(value => isValidDate(value), 'Enter a valid original date'),
]);
export const medicareExpiry = z.string().refine(value => {
  const parsedDate = parse(`01/${value}`, 'dd/MM/yyyy', new Date());
  return isFuture(parsedDate) && Math.abs(differenceInYears(addYears(new Date(), 10), parsedDate)) <= 10;
}, 'Enter a valid expiry date in the future');

export const expiryBNPL = z.union([
  z
    .date({
      required_error: requiredFieldError(EXPIRY_DATE),
      invalid_type_error: requiredFieldError(EXPIRY_DATE),
    })
    .refine(value => isFuture(value), 'You must enter future expiry date')
    .refine(value => {
      return addYears(new Date(), 20) >= value;
    }, 'Must expire within 20 years'),
  z.string().refine(value => isValidDate(value), 'Enter a valid date'),
]);

export const baseLicense = z.object({
  number: requiredNumberString(LICENCE_NUMBER),
  cardNumber: requiredNumberString(LICENCE_CARD_NUMBER),
  state: z.nativeEnum(AddressState, {
    errorMap: () => ({
      message: requiredFieldError('State'),
    }),
  }),
  expiryDate: expiry,
});

export const license = baseLicense.superRefine((data, ctx) => {
  const { state, cardNumber, number } = data;
  const { isValid, error } = validateDriversLicenseCardNumber(state, cardNumber, number);
  if (!isValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: error,
      path: ['cardNumber'],
    });
  }
});

export const passport = z.object({
  number: requiredString(PASSPORT_NUMBER),
  issuingCountry: requiredString(ISSUING_COUNTRY),
  expiryDate: expiry,
});

export const medicare = z.object({
  number: z
    .string({
      required_error: requiredFieldError(MEDICARE_NUMBER),
    })
    .transform((val, ctx) => {
      const trimmed = val.trim();
      if (!/^\d{10}$/.test(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid medicare number',
        });
        return z.NEVER;
      }
      return trimmed;
    }),
  reference: z
    .string({
      required_error: requiredFieldError(MEDICARE_REFERENCE),
    })
    .transform((val, ctx) => {
      const parsed = parseInt(val);
      if (isNaN(parsed) || val.length != 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid reference number',
        });
        return z.NEVER;
      }
      return val;
    }),
  color: z.nativeEnum(MedicareCardColour, {
    errorMap: () => ({
      message: requiredFieldError(),
    }),
  }),
  expiryDate: medicareExpiry,
});

export const photoId = z.object({
  number: requiredString(PHOTO_ID_NUMBER),
  state: z.nativeEnum(AddressState, {
    errorMap: () => ({
      message: requiredFieldError('State'),
    }),
  }),
});

export const identification = z.object({
  type: z.nativeEnum(IdentificationType, {
    errorMap: () => ({
      message: requiredFieldError(IDENTIFICATION_TYPE),
    }),
  }),
  passport: passport.optional(),
  drivingLicense: license.optional(),
  medicare: medicare.optional(),
  photoId: photoId.optional(),
});
